import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ProdutosDTO } from '../../../models/produto.models';
import { EtiquetaItemFormDTO, TipoImpressaoEtiqueta } from '../../../models/produtos/etiqueta-item.model';
import { ProdutosService } from '../../../services/produtos/produto.service';

// pdfjs-dist e carregado via dynamic import para evitar pre-bundling do Vite
// (o pre-bundling gera warning de "dynamic import cannot be analyzed by Vite"
// pois o proprio pdfjs-dist carrega seu worker via import dinamico).
// Worker servido a partir de /assets/pdfjs/pdf.worker.min.mjs (configurado em angular.json)
type PdfjsModule = typeof import('pdfjs-dist');
let pdfjsLibPromise: Promise<PdfjsModule> | null = null;

function loadPdfjs(): Promise<PdfjsModule> {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import('pdfjs-dist').then((mod) => {
      (mod as any).GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.mjs';
      return mod;
    });
  }
  return pdfjsLibPromise;
}

@Component({
  selector: 'app-etiqueta-item',
  templateUrl: './etiqueta-item.component.html',
  styleUrls: ['./etiqueta-item.component.scss'],
})
export class EtiquetaItemComponent implements OnChanges, OnDestroy {

  @Input() visible: boolean = false;
  @Input() produto: ProdutosDTO | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Estado do formulario
  form: EtiquetaItemFormDTO = this.createEmptyForm();

  // Estado do dialog
  loading: boolean = false;
  pdfGerado: boolean = false;
  pdfUrl: string | null = null;
  pdfUrlSafe: SafeResourceUrl | null = null;

  // Preview como imagem (renderizada via pdfjs-dist)
  pdfImagemPreviewUrl: SafeResourceUrl | null = null;
  loadingPreview: boolean = false;

  // Lightbox de imagem expandida
  imagemExpandidaVisible: boolean = false;

  // Opcoes do dropdown
  tiposImpressao: { label: string; value: TipoImpressaoEtiqueta }[] = [];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    titulo: '',
    subtitulo: '',
    gerando_pdf: '',
    erro_gerar_pdf: '',
    carregando_preview: '',
    clique_para_ampliar: '',
    titulo_imagem_expandida: '',
  };

  labels = {
    item_selecionado: '',
    tipo_folha: '',
    quantidade: '',
    quantidade_impressa: '',
  };

  placeholders = {
    selecione_tipo: '',
  };

  botoes = {
    gerar: '',
    imprimir: '',
    download: '',
    cancelar: '',
    limpar: '',
    fechar: '',
  };

  // Flags de validacao visual (exibidas apos tentativa de submit)
  erros = {
    tipoImpressao: false,
    quantidade: false,
    quantidadeImpressa: false,
  };

  validacoes = {
    tipo_obrigatorio: '',
    quantidade_obrigatoria: '',
    quantidade_minima: '',
    quantidade_maxima: '',
    quantidade_impressa_obrigatoria: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private produtosService: ProdutosService,
    private sanitizer: DomSanitizer,
  ) {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Resetar estado ao abrir o modal apenas quando nao ha PDF gerado
    // (evita limpar o preview logo apos gerar o PDF)
    if (changes['visible']?.currentValue === true && changes['visible']?.previousValue === false) {
      if (!this.pdfGerado) {
        this.revogarPdfUrl();
        this.form = this.createEmptyForm();
        this.erros = { tipoImpressao: false, quantidade: false, quantidadeImpressa: false };
        this.loading = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.revogarPdfUrl();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =========================================================================
  // Acoes
  // =========================================================================

  submit(): void {
    if (!this.validar()) return;
    if (!this.produto?.id) return;

    const tipo = this.form.tipoImpressao!.value;
    const quantidade = this.form.quantidade!;
    const quantidadeImpressa = tipo === TipoImpressaoEtiqueta.A4
      ? (this.form.quantidadeImpressa ?? 0)
      : undefined;

    this.loading = true;

    this.produtosService.gerarEtiquetaPdf(
      this.produto.id,
      tipo,
      quantidade,
      quantidadeImpressa,
    ).subscribe({
      next: (base64: string) => {
        this.revogarPdfUrl();
        const blob = this.base64ToPdfBlob(base64);
        this.pdfUrl = URL.createObjectURL(blob);
        this.pdfUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfUrl);
        this.pdfGerado = true;
        this.loading = false;

        // Disparar geracao da imagem de preview (assincrono)
        this.gerarImagemPreview(blob);
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.erro_gerar_pdf,
        });
      },
    });
  }

  imprimir(): void {
    if (this.pdfUrl) {
      // Abrir o PDF em nova janela para o usuario imprimir via dialog do browser
      window.open(this.pdfUrl, '_blank');
    }
  }

  download(): void {
    if (!this.pdfUrl) return;

    const nomeItem = this.produto?.nome?.replace(/\s+/g, '-') || 'etiqueta';
    const a = document.createElement('a');
    a.href = this.pdfUrl;
    a.download = `etiqueta-${nomeItem}.pdf`;
    a.click();
  }

  limpar(): void {
    this.form = this.createEmptyForm();
    this.erros = { tipoImpressao: false, quantidade: false, quantidadeImpressa: false };
  }

  fechar(): void {
    this.revogarPdfUrl();
    this.form = this.createEmptyForm();
    this.erros = { tipoImpressao: false, quantidade: false, quantidadeImpressa: false };
    this.pdfGerado = false;
    this.loading = false;
    this.imagemExpandidaVisible = false;
    this.visibleChange.emit(false);
  }

  onDialogShow(): void {
    // Reset apenas se nao ha PDF gerado — evita limpar o preview recem-criado
    if (!this.pdfGerado) {
      this.revogarPdfUrl();
      this.form = this.createEmptyForm();
      this.erros = { tipoImpressao: false, quantidade: false, quantidadeImpressa: false };
      this.loading = false;
    }
  }

  onDialogHide(): void {
    this.fechar();
  }

  abrirImagemExpandida(): void {
    if (this.pdfImagemPreviewUrl) {
      this.imagemExpandidaVisible = true;
    }
  }

  fecharImagemExpandida(): void {
    this.imagemExpandidaVisible = false;
  }

  get isA4(): boolean {
    return this.form.tipoImpressao?.value === TipoImpressaoEtiqueta.A4;
  }

  // =========================================================================
  // Validacao
  // =========================================================================

  private validar(): boolean {
    // Resetar flags antes de revalidar
    this.erros = { tipoImpressao: false, quantidade: false, quantidadeImpressa: false };

    if (!this.form.tipoImpressao) {
      this.erros.tipoImpressao = true;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.validacoes.tipo_obrigatorio,
      });
      return false;
    }

    if (!this.form.quantidade || this.form.quantidade < 1) {
      this.erros.quantidade = true;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.form.quantidade === null || this.form.quantidade === undefined
          ? this.validacoes.quantidade_obrigatoria
          : this.validacoes.quantidade_minima,
      });
      return false;
    }

    if (this.form.quantidade > 270) {
      this.erros.quantidade = true;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.validacoes.quantidade_maxima,
      });
      return false;
    }

    if (this.isA4 && (this.form.quantidadeImpressa === null || this.form.quantidadeImpressa === undefined)) {
      this.erros.quantidadeImpressa = true;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.validacoes.quantidade_impressa_obrigatoria,
      });
      return false;
    }

    return true;
  }

  // =========================================================================
  // Renderizacao da primeira pagina como imagem (pdfjs-dist)
  // =========================================================================

  private async gerarImagemPreview(blob: Blob): Promise<void> {
    this.loadingPreview = true;
    this.pdfImagemPreviewUrl = null;

    try {
      const pdfjsLib = await loadPdfjs();
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = (pdfjsLib as any).getDocument({ data: new Uint8Array(arrayBuffer) });
      const pdfDocument = await loadingTask.promise;
      const page = await pdfDocument.getPage(1);

      // Resolucao do preview: scale 2 produz boa qualidade visual sem peso excessivo
      const viewport = page.getViewport({ scale: 2 });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        this.loadingPreview = false;
        return;
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      const dataUrl = canvas.toDataURL('image/png');
      this.pdfImagemPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
      this.loadingPreview = false;
    } catch (err) {
      this.loadingPreview = false;
      // Em caso de falha no render, mantem o estado de PDF gerado para que o usuario
      // ainda consiga usar Imprimir / Descargar
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.erro_gerar_pdf,
      });
    }
  }

  // =========================================================================
  // Utilitarios
  // =========================================================================

  private createEmptyForm(): EtiquetaItemFormDTO {
    return {
      tipoImpressao: undefined,
      quantidade: 1,
      quantidadeImpressa: 0,
    };
  }

  private base64ToPdfBlob(base64: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'application/pdf' });
  }

  private revogarPdfUrl(): void {
    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = null;
      this.pdfUrlSafe = null;
    }
    this.pdfImagemPreviewUrl = null;
    this.loadingPreview = false;
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('etiqueta_item.titulo'),
      subtitulo: this.translate.instant('etiqueta_item.subtitulo'),
      gerando_pdf: this.translate.instant('etiqueta_item.mensagens.gerando_pdf'),
      erro_gerar_pdf: this.translate.instant('etiqueta_item.mensagens.erro_gerar_pdf'),
      carregando_preview: this.translate.instant('etiqueta_item.mensagens.carregando_preview'),
      clique_para_ampliar: this.translate.instant('etiqueta_item.mensagens.clique_para_ampliar'),
      titulo_imagem_expandida: this.translate.instant('etiqueta_item.mensagens.titulo_imagem_expandida'),
    };

    this.labels = {
      item_selecionado: this.translate.instant('etiqueta_item.labels.item_selecionado'),
      tipo_folha: this.translate.instant('etiqueta_item.labels.tipo_folha'),
      quantidade: this.translate.instant('etiqueta_item.labels.quantidade'),
      quantidade_impressa: this.translate.instant('etiqueta_item.labels.quantidade_impressa'),
    };

    this.placeholders = {
      selecione_tipo: this.translate.instant('etiqueta_item.placeholders.selecione_tipo'),
    };

    this.botoes = {
      gerar: this.translate.instant('etiqueta_item.botoes.gerar'),
      imprimir: this.translate.instant('etiqueta_item.botoes.imprimir'),
      download: this.translate.instant('etiqueta_item.botoes.download'),
      cancelar: this.translate.instant('etiqueta_item.botoes.cancelar'),
      limpar: this.translate.instant('etiqueta_item.botoes.limpar'),
      fechar: this.translate.instant('etiqueta_item.botoes.fechar'),
    };

    this.validacoes = {
      tipo_obrigatorio: this.translate.instant('etiqueta_item.validacoes.tipo_obrigatorio'),
      quantidade_obrigatoria: this.translate.instant('etiqueta_item.validacoes.quantidade_obrigatoria'),
      quantidade_minima: this.translate.instant('etiqueta_item.validacoes.quantidade_minima'),
      quantidade_maxima: this.translate.instant('etiqueta_item.validacoes.quantidade_maxima'),
      quantidade_impressa_obrigatoria: this.translate.instant('etiqueta_item.validacoes.quantidade_impressa_obrigatoria'),
    };

    this.buildDropdownOptions();
  }

  private buildDropdownOptions(): void {
    this.tiposImpressao = [
      { label: this.translate.instant('etiqueta_item.opcoes.bobina_58'), value: TipoImpressaoEtiqueta.BOBINA_58 },
      { label: this.translate.instant('etiqueta_item.opcoes.bobina_80'), value: TipoImpressaoEtiqueta.BOBINA_80 },
      { label: this.translate.instant('etiqueta_item.opcoes.folha_a4'), value: TipoImpressaoEtiqueta.A4 },
    ];
  }
}
